// Store layout (Implementation Plan v02 §5.1, §6.1–6.3; I-1a Batch 3).
//
// Two version numbers, on purpose:
// - DB_VERSION is the IndexedDB structure (object stores). It changes rarely.
// - DATA_SCHEMA_VERSION is the data-schema version of the records (Plan §6.3),
//   bound into every record's AAD and moved forward by the migration runner.
//
// Object stores:
//   records       sealed diary records (one clear kind `record`; decision 1)
//   blobs         sealed photo blobs (own key per photo)
//   keyring       wraps of the data key ('app-lock', 'recovery')
//   meta          clear: install record, data-schema version, pre-migration note
//   premigration  copy of `records` taken before a migration; cleared on success

import { type DBSchema, type IDBPDatabase, openDB } from 'idb'

import type { KeyWrap, SealedRecord, WrapPurpose } from '../crypto'

import type { InstallRecord } from './installRecord'

export const DB_NAME = 'pebble-store'
export const DB_VERSION = 1
export const DATA_SCHEMA_VERSION = 1

export const META_DATA_SCHEMA = 'dataSchemaVersion'
export const META_INSTALL = 'install'
export const META_PREMIGRATION = 'premigration'

export interface PremigrationNote {
  readonly fromVersion: number
  readonly toVersion: number
}

export type MetaValue = number | InstallRecord | PremigrationNote

export interface StoreSchema extends DBSchema {
  records: { key: string; value: SealedRecord }
  blobs: { key: string; value: SealedRecord }
  keyring: { key: WrapPurpose; value: KeyWrap }
  meta: { key: string; value: MetaValue }
  premigration: { key: string; value: SealedRecord }
}

export type StoreDb = IDBPDatabase<StoreSchema>

export interface OpenDbHooks {
  /**
   * Another copy wants to delete the database or upgrade it (versionchange).
   * The handler must close this connection and drop decrypted state, or the other
   * copy waits forever. Shipped in the first release because old copies will be
   * running this exact code when a later version upgrades.
   */
  readonly onBlocking?: () => void
  /** The browser closed the connection abnormally (e.g. storage cleared). */
  readonly onTerminated?: () => void
}

export function openStoreDb(name: string = DB_NAME, hooks: OpenDbHooks = {}): Promise<StoreDb> {
  let opened: StoreDb | undefined
  const promise = openDB<StoreSchema>(name, DB_VERSION, {
    blocking() {
      if (hooks.onBlocking) {
        hooks.onBlocking()
      } else {
        opened?.close()
      }
    },
    terminated() {
      hooks.onTerminated?.()
    },
    upgrade(db) {
      if (!db.objectStoreNames.contains('records')) {
        db.createObjectStore('records', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('blobs')) {
        db.createObjectStore('blobs', { keyPath: 'id' })
      }
      if (!db.objectStoreNames.contains('keyring')) {
        db.createObjectStore('keyring', { keyPath: 'purpose' })
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta')
      }
      if (!db.objectStoreNames.contains('premigration')) {
        db.createObjectStore('premigration', { keyPath: 'id' })
      }
    },
  })
  return promise.then((db) => {
    opened = db
    return db
  })
}

/**
 * A transaction's completion promise with a handler already attached, so a
 * failed request never leaves `tx.done` as an unhandled rejection. Await it at
 * the end of the transaction as usual.
 */
export function watchDone(tx: { readonly done: Promise<void> }): Promise<void> {
  const done = tx.done
  done.catch(() => undefined)
  return done
}

/** True for the browser's "storage is full" failure (thrown, or as a transaction error). */
export function isQuotaError(error: unknown): boolean {
  return errorName(error) === 'QuotaExceededError'
}

// "Storage full" when the browser does not say so (I-1a Batch 6c). A full disk
// may surface as an UnknownError, or as a bare AbortError (`idb` rejects an
// aborted transaction that carries no error with one). Those names also have
// other causes, so they count as full only when the browser's own estimate
// leaves less room than the write needed plus a margin. The phone check could
// not run (USB blocked on the PC, 2026-10-27): this covers the likely names.
const MAYBE_FULL_NAMES: ReadonlySet<string> = new Set(['UnknownError', 'AbortError'])

/** Room the browser must still report, beyond the write, for an unclear failure NOT to count as full. */
export const LOW_SPACE_MARGIN = 5 * 1024 * 1024

/** The one call of StorageManager used here (injectable for tests; null = never ask). */
export type SpaceEstimate = Pick<StorageManager, 'estimate'>

export function browserSpaceEstimate(): SpaceEstimate | null {
  return typeof navigator !== 'undefined' && navigator.storage ? navigator.storage : null
}

/**
 * True when a failed write was refused for space: always for QuotaExceededError;
 * for UnknownError / AbortError only when the estimate shows too little room
 * left for `neededBytes`. Never throws — no estimate means "not full".
 */
export async function isStorageFullError(error: unknown, neededBytes: number, space: SpaceEstimate | null): Promise<boolean> {
  if (isQuotaError(error)) {
    return true
  }
  const name = errorName(error)
  if (name === undefined || !MAYBE_FULL_NAMES.has(name) || !space) {
    return false
  }
  try {
    const { usage, quota } = await space.estimate()
    if (usage === undefined || quota === undefined) {
      return false
    }
    return quota - usage < neededBytes + LOW_SPACE_MARGIN
  } catch {
    return false
  }
}

/** The error's name, also for a DOMException that is not an `Error` in an older engine. */
function errorName(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined
  }
  const name = (error as { readonly name?: unknown }).name
  return typeof name === 'string' ? name : undefined
}
